import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureEnabled } from '../../components/FeatureGate';
import './OrdersManagement.css';

import {
    DeliveryOrder,
    DineInContext,
    OrderChannel,
    OrderChannelEnum,
    OrderCustomer,
    OrderItem,
    OrderNotes,
    OrderStatus,
    OrderStatusEnum,
    OrderTimelineEntry,
    PaymentStatus,
    PaymentStatusEnum,
    TakeawayContext,
} from './ordersTypes';
import type { ApiOrderBoardOrder } from '../../api/ordersApi';
import {
    fetchOrdersBoard,
    normalizeApiStatus,
    normalizeChannel,
    normalizePaymentStatus,
    updateOrderStatus as apiUpdateOrderStatus,
    registerOrderPayment,
} from '../../api/ordersApi';
import {
    PrintDestinationDTO,
    PrintPreviewDTO,
    PrintTemplateDTO,
    fetchPrintPreview,
    fetchPrintPreviewPdf,
    listPrintDestinations,
    listPrintTemplates,
    manualReprintOrder,
} from '../../api/printApi';

const TEMPLATE_TYPE_FROM_KIND: Record<PrintTemplateKind, string> = {
    BUSINESS: 'FULL_ORDER',
    KITCHEN: 'KITCHEN',
    RECEIPT: 'RECEIPT',
};

const DESTINATION_TYPE_FROM_KIND: Record<PrintTemplateKind, string> = {
    BUSINESS: 'FULL_ORDER',
    KITCHEN: 'KITCHEN',
    RECEIPT: 'RECEIPT',
};

const registerPaymentMethods = [
    { value: 'POS', label: 'POS' },
    { value: 'CASH', label: 'Contanti' },
    { value: 'ONLINE', label: 'Online' },
    { value: 'OTHER', label: 'Altro' },
];

type PrintTemplateKind = 'BUSINESS' | 'KITCHEN' | 'RECEIPT';

type PrintTemplate = {
    key: PrintTemplateKind;
    title: string;
    description: string;
    suggestedDestination: string;
};

const printTemplates: PrintTemplate[] = [
    {
        key: 'BUSINESS',
        title: 'Comanda completa',
        description: 'Documento completo destinato alla sala o al back-office.',
        suggestedDestination: 'Stampante di sala',
    },
    {
        key: 'KITCHEN',
        title: 'Comanda cucina',
        description: 'Contiene le preparazioni per le linee di cucina.',
        suggestedDestination: 'Stampante di cucina',
    },
    {
        key: 'RECEIPT',
        title: 'Ricevuta cliente',
        description: 'Versione destinata al cliente con riepilogo del conto.',
        suggestedDestination: 'Stampante fiscale o ricevute',
    },
];

const statusLabels: Record<OrderStatus, string> = {
    NEW: 'Da confermare',
    ACKNOWLEDGED: 'Confermato',
    PREPARING: 'In preparazione',
    READY: 'Pronto',
    ON_DELIVERY: 'In consegna',
    DELIVERED: 'Consegnato',
    CANCELLED: 'Annullato',
};

const statusFilterOrder: OrderStatus[] = [
    OrderStatusEnum.NEW,
    OrderStatusEnum.ACKNOWLEDGED,
    OrderStatusEnum.PREPARING,
    OrderStatusEnum.READY,
    OrderStatusEnum.ON_DELIVERY,
    OrderStatusEnum.DELIVERED,
];

const channelLabels: Record<OrderChannel, string> = {
    [OrderChannelEnum.DELIVERY]: 'Delivery',
    [OrderChannelEnum.DINE_IN]: 'Sala',
    [OrderChannelEnum.TAKEAWAY]: 'Take-away',
};

const channelOrder: OrderChannel[] = [
    OrderChannelEnum.DELIVERY,
    OrderChannelEnum.DINE_IN,
    OrderChannelEnum.TAKEAWAY,
];

type PrintPreviewPayload = {
    schemaVersion?: number;
    destinationType?: string;
    locale?: string;
    orderId?: number;
    orderCode?: string;
    totals?: Record<string, unknown> | null;
    items?: Array<Record<string, unknown>> | null;
    customer?: Record<string, unknown> | null;
    tenant?: Record<string, unknown> | null;
} | null;

function coerceNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    if (typeof value === 'bigint') {
        return Number(value);
    }
    return null;
}

function formatCurrencySafe(value: unknown, fallback = '—') {
    const numeric = coerceNumber(value);
    if (numeric === null) {
        return fallback;
    }
    return formatCurrency(numeric);
}

function getStringField(source: Record<string, unknown> | null | undefined, key: string) {
    if (!source) {
        return undefined;
    }
    const value = source[key];
    if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
    }
    return undefined;
}

function getNumberField(source: Record<string, unknown> | null | undefined, key: string) {
    if (!source) {
        return null;
    }
    return coerceNumber(source[key]);
}

function mapOrderFromApi(apiOrder: ApiOrderBoardOrder): DeliveryOrder {
    const channel = normalizeChannel(apiOrder.channel) ?? OrderChannelEnum.DELIVERY;
    const status = normalizeApiStatus(apiOrder.status) ?? OrderStatusEnum.NEW;
    const paymentStatus = normalizePaymentStatus(apiOrder.paymentStatus) ?? PaymentStatusEnum.UNPAID;
    const createdAt = apiOrder.createdAt ?? new Date().toISOString();
    const scheduledAt = apiOrder.scheduledAt ?? undefined;

    const items = (apiOrder.items ?? []).map((item, index): OrderItem => {
        const quantity = typeof item.quantity === 'number' && Number.isFinite(item.quantity) ? item.quantity : 1;
        const price = typeof item.price === 'number' && Number.isFinite(item.price) ? item.price : 0;
        const total = typeof item.total === 'number' && Number.isFinite(item.total)
            ? item.total
            : price * quantity;
        return {
            id: item.id ?? `${apiOrder.id}-item-${index}`,
            name: item.name ?? 'Articolo',
            quantity,
            price,
            total,
            modifiers: item.modifiers && item.modifiers.length > 0 ? item.modifiers : undefined,
            category: item.category ?? undefined,
            station: item.station ?? undefined,
        };
    });

    const total = typeof apiOrder.total === 'number' && Number.isFinite(apiOrder.total)
        ? apiOrder.total
        : items.reduce((acc, item) => acc + item.total, 0);

    const customer = buildCustomer(apiOrder, channel);
    const notes = buildNotes(apiOrder.notes);
    const timeline = buildTimeline(apiOrder.timeline, createdAt, status);
    const dineIn = mapDineIn(apiOrder.dineIn);
    const takeaway = mapTakeaway(apiOrder.takeaway);

    return {
        id: `${channel.toLowerCase()}-${apiOrder.id}`,
        backendId: String(apiOrder.id),
        code: apiOrder.code ?? `${channel}-${apiOrder.id}`,
        createdAt,
        scheduledAt: scheduledAt ?? undefined,
        status,
        channel,
        paymentMethod: apiOrder.paymentMethod ?? 'Manuale',
        paymentStatus,
        total,
        serviceFee: typeof apiOrder.serviceFee === 'number' && Number.isFinite(apiOrder.serviceFee)
            ? apiOrder.serviceFee
            : undefined,
        items,
        customer,
        notes,
        timeline,
        tags: apiOrder.tags ?? [],
        courier: undefined,
        dineIn,
        takeaway,
    };
}

function buildCustomer(apiOrder: ApiOrderBoardOrder, channel: OrderChannel): OrderCustomer {
    const addressSource = apiOrder.customer?.address;
    const line1 = addressSource?.line1;
    const city = addressSource?.city;
    const postalCode = addressSource?.postalCode;
    const hasAddress = Boolean(line1 && city && postalCode);
    const fallbackName = channel === OrderChannelEnum.DINE_IN && apiOrder.dineIn?.table
        ? `Tavolo ${apiOrder.dineIn.table}`
        : 'Cliente';

    return {
        name: apiOrder.customer?.name ?? fallbackName,
        phone: apiOrder.customer?.phone ?? undefined,
        email: apiOrder.customer?.email ?? undefined,
        taxId: apiOrder.customer?.taxId ?? undefined,
        companyName: apiOrder.customer?.companyName ?? undefined,
        address: hasAddress
            ? {
                line1: line1!,
                line2: addressSource?.line2 ?? undefined,
                city: city!,
                postalCode: postalCode!,
                notes: addressSource?.notes ?? undefined,
            }
            : undefined,
    };
}

function buildNotes(notes?: ApiOrderBoardOrder['notes']): OrderNotes | undefined {
    if (!notes) {
        return undefined;
    }
    const cleaned: OrderNotes = {};
    if (notes.customer) cleaned.customer = notes.customer;
    if (notes.kitchen) cleaned.kitchen = notes.kitchen;
    if (notes.delivery) cleaned.delivery = notes.delivery;
    if (notes.billing) cleaned.billing = notes.billing;
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

function buildTimeline(
    timeline: ApiOrderBoardOrder['timeline'],
    createdAt: string,
    fallbackStatus: OrderStatus,
): OrderTimelineEntry[] {
    const entries = (timeline ?? []).map(entry => ({
        at: entry.at ?? createdAt,
        label: entry.label ?? 'Aggiornamento stato',
        status: normalizeApiStatus(entry.status) ?? fallbackStatus,
        note: entry.note ?? undefined,
    }));
    if (entries.length === 0) {
        entries.push({
            at: createdAt,
            label: 'Ordine registrato',
            status: fallbackStatus,
            note: undefined,
        });
    }
    return entries;
}

function mapDineIn(dineIn?: ApiOrderBoardOrder['dineIn']): DineInContext | null {
    if (!dineIn) {
        return null;
    }
    return {
        table: dineIn.table ?? undefined,
        covers: typeof dineIn.covers === 'number' && Number.isFinite(dineIn.covers) ? dineIn.covers : undefined,
        area: dineIn.area ?? undefined,
        waiter: dineIn.waiter ?? undefined,
        seatings: dineIn.seatings ?? undefined,
        note: dineIn.note ?? undefined,
        checkInAt: dineIn.checkInAt ?? undefined,
    };
}

function mapTakeaway(takeaway?: ApiOrderBoardOrder['takeaway']): TakeawayContext | null {
    if (!takeaway) {
        return null;
    }
    return {
        pickupAt: takeaway.pickupAt ?? undefined,
        pickupName: takeaway.pickupName ?? undefined,
        contactless: takeaway.contactless ?? undefined,
        packagingNotes: takeaway.packagingNotes ?? undefined,
    };
}

function formatCurrency(amount: number, locale = 'it-IT', currency = 'EUR') {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

function formatTime(value: string) {
    const date = new Date(value);
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(value: string) {
    const date = new Date(value);
    return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function resolveStatusChip(status: OrderStatus) {
    return `order-status order-status--${status.toLowerCase()}`;
}

function resolvePaymentChip(paymentStatus: PaymentStatus) {
    return `payment-status payment-status--${paymentStatus.toLowerCase()}`;
}

function resolveChannelLabel(channel: OrderChannel) {
    return channelLabels[channel];
}

function getEscalationAction(order: DeliveryOrder): { label: string; status: OrderStatus } {
    switch (order.channel) {
        case 'DELIVERY':
            return { label: 'Affida al rider', status: 'ON_DELIVERY' };
        case 'TAKEAWAY':
            return { label: 'Pronto per ritiro', status: 'READY' };
        case 'DINE_IN':
        default:
            return { label: 'Servito al tavolo', status: 'READY' };
    }
}

const statusFilterLabels: Record<OrderStatus, string> = {
    NEW: 'Da confermare',
    ACKNOWLEDGED: 'Confermati',
    PREPARING: 'In preparazione',
    READY: 'Pronti',
    ON_DELIVERY: 'In consegna',
    DELIVERED: 'Consegnati',
    CANCELLED: 'Annullati',
};

const OrdersManagementPage: React.FC = () => {
    const navigate = useNavigate();
    const printCenterEnabled = useFeatureEnabled('printCenterEnabled');

    const [orders, setOrders] = useState<DeliveryOrder[]>([]);
    const [selectedOrderId, setSelectedOrderId] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<OrderStatus | 'ALL'>('ALL');
    const [channelFilter, setChannelFilter] = useState<OrderChannel | 'ALL'>('ALL');
    const [autoPrintTemplates, setAutoPrintTemplates] = useState<Record<PrintTemplateKind, boolean>>({
        BUSINESS: false,
        KITCHEN: true,
        RECEIPT: true,
    });
    const [printDestinations, setPrintDestinations] = useState<PrintDestinationDTO[]>([]);
    const [availablePrintTemplates, setAvailablePrintTemplates] = useState<PrintTemplateDTO[]>([]);
    const [printConfigLoading, setPrintConfigLoading] = useState<boolean>(false);
    const [printConfigError, setPrintConfigError] = useState<string | null>(null);
    const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
        if (typeof window === 'undefined') {
            return true;
        }
        const stored = window.localStorage.getItem('orders.soundEnabled');
        if (stored === '0') {
            return false;
        }
        if (stored === '1') {
            return true;
        }
        return true;
    });
    const [selectedPreviewTemplate, setSelectedPreviewTemplate] = useState<PrintTemplateKind>('BUSINESS');
    const [previewData, setPreviewData] = useState<PrintPreviewDTO | null>(null);
    const [previewLoading, setPreviewLoading] = useState<boolean>(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [previewPdfLoading, setPreviewPdfLoading] = useState<boolean>(false);
    const [paymentModalOpen, setPaymentModalOpen] = useState<boolean>(false);
    const [paymentModalSubmitting, setPaymentModalSubmitting] = useState<boolean>(false);
    const [paymentModalError, setPaymentModalError] = useState<string | null>(null);
    const [paymentModalForm, setPaymentModalForm] = useState({ amount: '', method: 'POS', reference: '', note: '' });
    const [adminNotice, setAdminNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const knownOrderIdsRef = useRef<Set<string>>(new Set());
    const initialLoadRef = useRef<boolean>(true);
    const audioContextRef = useRef<AudioContext | null>(null);

    const selectedOrder = useMemo(
        () => orders.find(order => order.id === selectedOrderId) ?? null,
        [orders, selectedOrderId]
    );

    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            const matchStatus = statusFilter === 'ALL' || order.status === statusFilter;
            const matchChannel = channelFilter === 'ALL' || order.channel === channelFilter;
            return matchStatus && matchChannel;
        });
    }, [orders, statusFilter, channelFilter]);

    const summary = useMemo(() => {
        return orders.reduce(
            (acc, order) => {
                acc.total += 1;
                acc.byStatus[order.status] = (acc.byStatus[order.status] ?? 0) + 1;
                if (order.status === OrderStatusEnum.NEW || order.status === OrderStatusEnum.ACKNOWLEDGED) {
                    acc.pendingRevenue += order.total;
                }
                if (order.channel === OrderChannelEnum.DELIVERY) {
                    acc.deliveryCount += 1;
                }
                if (order.channel === OrderChannelEnum.DINE_IN) {
                    acc.dineInCount += 1;
                }
                if (order.channel === OrderChannelEnum.TAKEAWAY) {
                    acc.takeawayCount += 1;
                }
                return acc;
            },
            {
                total: 0,
                pendingRevenue: 0,
                deliveryCount: 0,
                dineInCount: 0,
                takeawayCount: 0,
                byStatus: {} as Record<OrderStatus, number>,
            }
        );
    }, [orders]);

    const escalationAction = selectedOrder ? getEscalationAction(selectedOrder) : null;

    const templatesByKind = useMemo<Record<PrintTemplateKind, PrintTemplateDTO | null>>(() => {
        return {
            BUSINESS: availablePrintTemplates.find(template => template.templateType === TEMPLATE_TYPE_FROM_KIND.BUSINESS && template.active) ?? null,
            KITCHEN: availablePrintTemplates.find(template => template.templateType === TEMPLATE_TYPE_FROM_KIND.KITCHEN && template.active) ?? null,
            RECEIPT: availablePrintTemplates.find(template => template.templateType === TEMPLATE_TYPE_FROM_KIND.RECEIPT && template.active) ?? null,
        };
    }, [availablePrintTemplates]);

    const destinationsByKind = useMemo<Record<PrintTemplateKind, PrintDestinationDTO[]>>(() => {
        const base: Record<PrintTemplateKind, PrintDestinationDTO[]> = {
            BUSINESS: [],
            KITCHEN: [],
            RECEIPT: [],
        };
        printDestinations.forEach(destination => {
            (Object.entries(DESTINATION_TYPE_FROM_KIND) as [PrintTemplateKind, string][]).forEach(([kind, type]) => {
                if (destination.destinationType === type && destination.enabled) {
                    base[kind].push(destination);
                }
            });
        });
        return base;
    }, [printDestinations]);

    useEffect(() => {
        if (!printCenterEnabled) {
            setPreviewData(null);
            setPreviewError('Centro stampa disabilitato per questo tenant');
            setPreviewLoading(false);
            return;
        }
        if (!selectedOrder) {
            setPreviewData(null);
            setPreviewError(null);
            setPreviewLoading(false);
            return;
        }
        const templateKind = selectedPreviewTemplate;
        const destinationType = DESTINATION_TYPE_FROM_KIND[templateKind];
        const templateInfo = templatesByKind[templateKind];
        let cancelled = false;
        setPreviewLoading(true);
        setPreviewError(null);
        (async () => {
            try {
                const preview = await fetchPrintPreview(selectedOrder.backendId, {
                    channel: selectedOrder.channel,
                    destinationType,
                    templateId: templateInfo?.id ?? undefined,
                });
                if (!cancelled) {
                    setPreviewData(preview);
                }
            } catch (err) {
                if (!cancelled) {
                    const message = err instanceof Error ? err.message : 'Anteprima non disponibile al momento';
                    setPreviewError(message);
                    setPreviewData(null);
                }
            } finally {
                if (!cancelled) {
                    setPreviewLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selectedOrder, selectedPreviewTemplate, templatesByKind, printCenterEnabled]);

    useEffect(() => {
        setAdminNotice(null);
    }, [selectedOrderId]);

    const handleOpenRegisterPayment = useCallback(() => {
        if (!selectedOrder) {
            return;
        }
        setAdminNotice(null);
        setPaymentModalError(null);
        setPaymentModalForm({
            amount: selectedOrder.total ? selectedOrder.total.toFixed(2) : '',
            method: selectedOrder.paymentMethod ?? 'POS',
            reference: '',
            note: '',
        });
        setPaymentModalOpen(true);
    }, [selectedOrder]);

    const handleCloseRegisterPayment = useCallback(() => {
        if (paymentModalSubmitting) {
            return;
        }
        setPaymentModalOpen(false);
    }, [paymentModalSubmitting]);

    const handleSubmitRegisterPayment = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!selectedOrder) {
            return;
        }
        setPaymentModalError(null);
        setPaymentModalSubmitting(true);

        const normalizedAmount = paymentModalForm.amount.replace(',', '.').trim();
        const numericAmount = Number(normalizedAmount);
        if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
            setPaymentModalError('Inserisci un importo valido.');
            setPaymentModalSubmitting(false);
            return;
        }

        const payload = {
            amount: Number(numericAmount.toFixed(2)),
            method: paymentModalForm.method || 'POS',
            reference: paymentModalForm.reference.trim() || undefined,
            note: paymentModalForm.note.trim() || undefined,
            receivedAt: new Date().toISOString(),
        };

        try {
            const updated = await registerOrderPayment(selectedOrder.channel, selectedOrder.backendId, payload);
            const mapped = mapOrderFromApi(updated);
            setOrders(prev => prev.map(order => (order.id === mapped.id ? mapped : order)));
            setSelectedOrderId(mapped.id);
            setPaymentModalOpen(false);
            setAdminNotice({ type: 'success', message: 'Incasso registrato correttamente.' });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile registrare l\'incasso.';
            setPaymentModalError(message);
        } finally {
            setPaymentModalSubmitting(false);
        }
    }, [paymentModalForm, selectedOrder, registerOrderPayment]);

    const handleSelectOrder = (orderId: string) => {
        setSelectedOrderId(orderId);
    };

    const ensureAudioContext = useCallback((): AudioContext | null => {
        if (typeof window === 'undefined') {
            return null;
        }
        const anyWindow = window as typeof window & { webkitAudioContext?: typeof AudioContext };
        const Ctx = anyWindow.AudioContext || anyWindow.webkitAudioContext;
        if (!Ctx) {
            console.warn('AudioContext non supportato dal browser.');
            return null;
        }
        if (!audioContextRef.current) {
            audioContextRef.current = new Ctx();
        }
        return audioContextRef.current;
    }, []);

    const playNotificationSound = useCallback((force = false) => {
        if (!force && !soundEnabled) {
            return;
        }
        const ctx = ensureAudioContext();
        if (!ctx) {
            return;
        }
        const startAt = ctx.currentTime;

        const scheduleBeep = (offset: number) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'square';
            osc.frequency.setValueAtTime(offset === 0 ? 880 : 660, startAt + offset);
            gain.gain.setValueAtTime(0.0001, startAt + offset);
            gain.gain.exponentialRampToValueAtTime(0.22, startAt + offset + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, startAt + offset + 0.28);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(startAt + offset);
            osc.stop(startAt + offset + 0.3);
        };

        scheduleBeep(0);
        scheduleBeep(0.35);
    }, [ensureAudioContext, soundEnabled]);

    const toggleSound = useCallback(async () => {
        const next = !soundEnabled;
        setSoundEnabled(next);
        try {
            if (typeof window !== 'undefined') {
                window.localStorage.setItem('orders.soundEnabled', next ? '1' : '0');
            }
        } catch (err) {
            console.warn('Impossibile salvare la preferenza audio', err);
        }
        if (next) {
            const ctx = ensureAudioContext();
            if (ctx && ctx.state === 'suspended') {
                try {
                    await ctx.resume();
                } catch (err) {
                    console.warn('Impossibile attivare l\'AudioContext', err);
                }
            }
            playNotificationSound(true);
        }
    }, [ensureAudioContext, playNotificationSound, soundEnabled]);

    const loadOrders = useCallback(async () => {
        setIsLoading(true);
        setLoadError(null);
        try {
            const response = await fetchOrdersBoard();
            const mapped = response.orders.map(mapOrderFromApi);
            const currentIds = new Set(mapped.map(order => order.id));
            if (!initialLoadRef.current) {
                const knownIds = knownOrderIdsRef.current;
                const newOrders = mapped.filter(order => !knownIds.has(order.id) && order.status === OrderStatusEnum.NEW);
                if (newOrders.length > 0) {
                    playNotificationSound();
                }
            }
            knownOrderIdsRef.current = currentIds;
            initialLoadRef.current = false;
            setOrders(mapped);
            if (mapped.length > 0) {
                setSelectedOrderId(prev => prev || mapped[0].id);
            } else {
                setSelectedOrderId('');
            }
        } catch (err) {
            console.error('Errore caricamento ordini', err);
            setLoadError(err instanceof Error ? err.message : 'Errore imprevisto nel caricamento ordini');
        } finally {
            setIsLoading(false);
        }
    }, [playNotificationSound]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const loadPrintConfiguration = useCallback(async () => {
        if (!printCenterEnabled) {
            setPrintDestinations([]);
            setAvailablePrintTemplates([]);
            setPrintConfigError(null);
            setPrintConfigLoading(false);
            return;
        }
        setPrintConfigLoading(true);
        setPrintConfigError(null);
        try {
            const [destList, templateList] = await Promise.all([
                listPrintDestinations(),
                listPrintTemplates(),
            ]);
            setPrintDestinations(destList);
            setAvailablePrintTemplates(templateList);
        } catch (err) {
            console.error('Errore caricamento configurazione stampa', err);
            setPrintConfigError(err instanceof Error ? err.message : 'Errore nel recupero della configurazione di stampa');
        } finally {
            setPrintConfigLoading(false);
        }
    }, [printCenterEnabled]);

    useEffect(() => {
        loadPrintConfiguration();
    }, [loadPrintConfiguration]);

    const handleOpenTemplateConfig = useCallback((kind: PrintTemplateKind) => {
        const params = new URLSearchParams();
        params.set('focus', 'template');
        params.set('templateType', TEMPLATE_TYPE_FROM_KIND[kind]);
        const templateInfo = templatesByKind[kind];
        if (templateInfo?.id) {
            params.set('templateId', String(templateInfo.id));
        }
        navigate(`/print-center?${params.toString()}`);
    }, [navigate, templatesByKind]);

    const handleOpenDestinationConfig = useCallback((kind: PrintTemplateKind) => {
        const params = new URLSearchParams();
        params.set('focus', 'destination');
        params.set('destinationType', DESTINATION_TYPE_FROM_KIND[kind]);
        const candidates = destinationsByKind[kind] ?? [];
        const preferred = candidates.find(dest => dest.isDefault) ?? candidates[0];
        if (preferred?.id) {
            params.set('destinationId', String(preferred.id));
        }
        navigate(`/print-center?${params.toString()}`);
    }, [navigate, destinationsByKind]);

    const handleStatusChange = useCallback(async (order: DeliveryOrder, nextStatus: OrderStatus) => {
        try {
            const updated = await apiUpdateOrderStatus(order.channel, order.backendId, nextStatus);
            const mapped = mapOrderFromApi(updated);
            setOrders(prev => prev.map(existing => (existing.id === order.id ? mapped : existing)));
        } catch (err) {
            console.error('Errore aggiornamento stato ordine', err);
            alert(err instanceof Error ? err.message : 'Impossibile aggiornare lo stato ordine');
        }
    }, []);

    const handlePrint = useCallback(async (order: DeliveryOrder, template: PrintTemplate) => {
        if (!printCenterEnabled) {
            alert('Centro stampa non disponibile per questo tenant.');
            return;
        }
        const templateInfo = templatesByKind[template.key];
        const availableDest = destinationsByKind[template.key] ?? [];

        if (!templateInfo) {
            alert('Nessun template attivo per questo tipo di stampa. Configuralo nel Centro stampa.');
            handleOpenTemplateConfig(template.key);
            return;
        }

        if (availableDest.length === 0) {
            alert('Configura prima almeno una stampante attiva nel Centro stampa.');
            handleOpenDestinationConfig(template.key);
            return;
        }

        let destination = availableDest[0];
        if (availableDest.length > 1) {
            const choice = window.prompt(
                'Seleziona la stampante per la ristampa (codice o ID):\n' +
                availableDest.map(dest => `${dest.code} (ID ${dest.id})`).join('\n'),
                destination.code || String(destination.id)
            );
            if (!choice) {
                return;
            }
            const normalized = choice.trim().toLowerCase();
            const selected = availableDest.find(dest => {
                const codeMatch = dest.code ? dest.code.toLowerCase() === normalized : false;
                const idMatch = String(dest.id) === choice.trim();
                return codeMatch || idMatch;
            });
            if (!selected) {
                alert('Stampante non riconosciuta.');
                return;
            }
            destination = selected;
        }

        try {
            const job = await manualReprintOrder(order.backendId, {
                destinationId: destination.id,
                templateId: templateInfo.id,
                channel: order.channel,
            });
            const channelLabel = resolveChannelLabel(order.channel);
            alert(`Ristampa ${channelLabel.toLowerCase()} in coda (job #${job.id}). Lo spooler locale eseguirà la stampa appena possibile.`);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile avviare la ristampa manuale.';
            alert(message);
        }
    }, [destinationsByKind, handleOpenDestinationConfig, handleOpenTemplateConfig, printCenterEnabled, templatesByKind]);

    const handlePreviewTemplateSelect = useCallback((template: PrintTemplateKind) => {
        setSelectedPreviewTemplate(template);
    }, []);

    const toggleAutoPrint = (template: PrintTemplateKind) => {
        if (!printCenterEnabled) {
            return;
        }
        setAutoPrintTemplates(prev => ({
            ...prev,
            [template]: !prev[template],
        }));
    };

    const handleDownloadPreviewPdf = useCallback(async () => {
        if (!printCenterEnabled) {
            return;
        }
        if (!selectedOrder) {
            return;
        }
        const templateKind = selectedPreviewTemplate;
        const destinationType = DESTINATION_TYPE_FROM_KIND[templateKind];
        const templateInfo = templatesByKind[templateKind];
        setPreviewPdfLoading(true);
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            setPreviewPdfLoading(false);
            return;
        }
        try {
            const blob = await fetchPrintPreviewPdf(selectedOrder.backendId, {
                channel: selectedOrder.channel,
                destinationType,
                templateId: templateInfo?.id ?? undefined,
            });
            const blobUrl = window.URL.createObjectURL(blob);
            const downloadAnchor = document.createElement('a');
            const rawCode = selectedOrder.code ?? selectedOrder.backendId ?? 'ordine';
            const normalizedCode = String(rawCode).replace(/[^a-zA-Z0-9_-]+/g, '-');
            downloadAnchor.href = blobUrl;
            downloadAnchor.download = `anteprima-${normalizedCode}.pdf`;
            document.body.appendChild(downloadAnchor);
            downloadAnchor.click();
            document.body.removeChild(downloadAnchor);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile scaricare il PDF dell\'anteprima.';
            alert(message);
        } finally {
            setPreviewPdfLoading(false);
        }
    }, [printCenterEnabled, selectedOrder, selectedPreviewTemplate, templatesByKind]);

    const previewPayload = (previewData?.payload ?? null) as PrintPreviewPayload;
    const previewRoot = (previewPayload ?? {}) as Record<string, unknown>;
    const previewItems = Array.isArray(previewPayload?.items)
        ? (previewPayload?.items as Record<string, unknown>[])
        : [];
    const previewTotals = (previewPayload?.totals ?? null) as Record<string, unknown> | null;
    const previewCustomer = (previewPayload?.customer ?? null) as Record<string, unknown> | null;
    const previewTenant = (previewPayload?.tenant ?? null) as Record<string, unknown> | null;
    const previewAddress = previewCustomer && typeof previewCustomer['address'] === 'object'
        ? (previewCustomer['address'] as Record<string, unknown>)
        : null;
    const previewBranding = previewData?.branding ?? null;
    const previewHtml = typeof previewData?.renderedHtml === 'string' && previewData.renderedHtml.trim().length > 0
        ? previewData.renderedHtml
        : null;
    const previewTimestamp = previewData ? formatDateTime(previewData.generatedAt) : null;
    const totalGross = getNumberField(previewTotals, 'orderTotalGross')
        ?? getNumberField(previewTotals, 'total')
        ?? getNumberField(previewTotals, 'itemsGross');
    const totalNet = getNumberField(previewTotals, 'orderTotalNet')
        ?? getNumberField(previewTotals, 'totalNet')
        ?? getNumberField(previewTotals, 'itemsNet');
    const totalVat = getNumberField(previewTotals, 'orderTotalVat')
        ?? getNumberField(previewTotals, 'totalVat')
        ?? getNumberField(previewTotals, 'itemsVat');
    const subtotalGross = getNumberField(previewTotals, 'subtotal')
        ?? getNumberField(previewTotals, 'itemsGross');
    const subtotalNet = getNumberField(previewTotals, 'subtotalNet')
        ?? getNumberField(previewTotals, 'itemsNet');
    const subtotalVat = getNumberField(previewTotals, 'subtotalVat')
        ?? getNumberField(previewTotals, 'itemsVat');
    const deliveryGross = getNumberField(previewTotals, 'deliveryFee');
    const deliveryNet = getNumberField(previewTotals, 'deliveryFeeNet');
    const deliveryVat = getNumberField(previewTotals, 'deliveryFeeVat');
    const vatRate = getNumberField(previewTotals, 'vatRate');

    const renderPreviewMeta = () => {
        if (!previewData) {
            return null;
        }
        const orderLabel = previewData.orderCode
            ?? (previewRoot['orderCode'] as string)
            ?? `Ordine #${previewData.orderId ?? ''}`;
        const channelLabel = resolveChannelLabel(previewData.channel as OrderChannel) ?? previewData.channel;
        const destinationLabel = previewData.destinationType;
        const tenantName = previewBranding?.tenantName
            ?? getStringField(previewTenant, 'tenantName')
            ?? getStringField(previewTenant, 'name')
            ?? getStringField(previewRoot, 'tenantName');
        const tenantSlug = getStringField(previewTenant, 'tenantSlug');
        const hasBrandInfo = Boolean(tenantName) || Boolean(tenantSlug) || Boolean(previewBranding?.logoUrl);
        return (
            <div className="print-preview__meta">
                <div className="print-preview__meta-block">
                    <strong>{orderLabel}</strong>
                    {previewTimestamp && <span>{previewTimestamp}</span>}
                </div>
                <div className="print-preview__meta-block">
                    <span>{channelLabel}</span>
                    <span>Destinazione {destinationLabel}</span>
                </div>
                {hasBrandInfo && (
                    <div className="print-preview__meta-block">
                        <span>{tenantName ?? 'Tenant'}</span>
                        {tenantSlug && (
                            <small>Slug: {tenantSlug}</small>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="orders-page">
            <header className="orders-header">
                <div>
                    <h1>Gestione Ordini Sala & Delivery</h1>
                    <p>Controlla in un unico pannello ordini delivery, take-away e in sala: coordina la brigata, assegna le consegne e genera le stampe operative.</p>
                </div>
                <div className="orders-header__actions">
                    <button
                        type="button"
                        className="orders-header__action"
                        onClick={loadOrders}
                        disabled={isLoading}
                    >
                        ⟳ Aggiorna feed
                    </button>
                    <button type="button" className="orders-header__action orders-header__action--primary">⚡ Avvia modalità servizio</button>
                    <button
                        type="button"
                        className={`orders-header__action ${soundEnabled ? 'orders-header__action--active' : ''}`}
                        onClick={toggleSound}
                        title={soundEnabled ? 'Disattiva avviso sonoro' : 'Attiva avviso sonoro'}
                    >
                        {soundEnabled ? '🔊 Suono attivo' : '🔈 Suono disattivato'}
                    </button>
                </div>
            </header>

            <div className="orders-body">
                <aside className="order-feed">
                    <div className="order-feed__filters">
                        <button
                            type="button"
                            className={statusFilter === 'ALL' ? 'active' : ''}
                            onClick={() => setStatusFilter('ALL')}
                        >
                            Tutti ({orders.length})
                        </button>
                        {statusFilterOrder.map(status => (
                            <button
                                key={status}
                                type="button"
                                className={statusFilter === status ? 'active' : ''}
                                onClick={() => setStatusFilter(status)}
                            >
                                {statusFilterLabels[status]} ({summary.byStatus[status] ?? 0})
                            </button>
                        ))}
                    </div>
                    <div className="order-feed__list">
                        {isLoading && (
                            <div className="order-feed__empty">
                                <p>Caricamento ordini in corso…</p>
                            </div>
                        )}

                        {!isLoading && loadError && orders.length === 0 && (
                            <div className="order-feed__empty">
                                <p>Errore: {loadError}</p>
                                <button type="button" onClick={loadOrders}>Riprova</button>
                            </div>
                        )}

                        {!isLoading && (!loadError || orders.length > 0) && filteredOrders.length === 0 && (
                            <div className="order-feed__empty">
                                <p>Nessun ordine in questa categoria.</p>
                            </div>
                        )}

                        {!isLoading && (!loadError || orders.length > 0) && filteredOrders.map(order => (
                            <article
                                key={order.id}
                                className={`order-card ${selectedOrder?.id === order.id ? 'order-card--active' : ''}`}
                                onClick={() => handleSelectOrder(order.id)}
                            >
                                <header className="order-card__header">
                                    <div>
                                        <strong>{order.code}</strong>
                                        <span className="order-card__time">{formatTime(order.createdAt)}</span>
                                    </div>
                                    <span className={resolveStatusChip(order.status)}>{statusLabels[order.status]}</span>
                                </header>
                                <div className="order-card__body">
                                    <div className="order-card__customer">
                                        <span>{order.customer.name}</span>
                                        {order.channel === 'DINE_IN' && order.dineIn && (
                                            <small>Tavolo {order.dineIn.table} · {order.dineIn.covers} coperti</small>
                                        )}
                                        {order.channel === 'TAKEAWAY' && order.takeaway?.pickupAt && (
                                            <small>Ritiro {formatTime(order.takeaway.pickupAt)}{order.takeaway.pickupName ? ` · ${order.takeaway.pickupName}` : ''}</small>
                                        )}
                                        {order.channel === 'DELIVERY' && order.customer.address && (
                                            <small>{order.customer.address.line1}</small>
                                        )}
                                        {order.scheduledAt && (
                                            <small className="order-card__schedule">Programmazione {formatTime(order.scheduledAt)}</small>
                                        )}
                                    </div>
                                    <div className="order-card__meta">
                                        <span className="order-card__channel">{resolveChannelLabel(order.channel)}</span>
                                        <span className="order-card__total">{formatCurrency(order.total)}</span>
                                        <span className={resolvePaymentChip(order.paymentStatus)}>{order.paymentStatus === 'PAID' ? 'Pagato' : 'Da incassare'}</span>
                                    </div>
                                </div>
                                {order.tags && order.tags.length > 0 && (
                                    <footer className="order-card__tags">
                                        {order.tags.map(tag => (
                                            <span key={tag}>{tag}</span>
                                        ))}
                                    </footer>
                                )}
                            </article>
                        ))}
                    </div>
                </aside>

                <main className="order-detail" aria-live="polite">
                    {isLoading && (
                        <div className="order-detail__placeholder">
                            <p>Caricamento dettagli ordine…</p>
                        </div>
                    )}

                    {!isLoading && loadError && !selectedOrder && (
                        <div className="order-detail__placeholder">
                            <p>Errore: {loadError}</p>
                            <button type="button" onClick={loadOrders}>Riprova</button>
                        </div>
                    )}

                    {!isLoading && !loadError && !selectedOrder && (
                        <div className="order-detail__placeholder">
                            <p>Seleziona un ordine per vedere i dettagli.</p>
                        </div>
                    )}

                    {!isLoading && selectedOrder && (
                        <div className="order-detail__content">
                            <header className="order-detail__header">
                                <div>
                                    <h2>Ordine {selectedOrder.code}</h2>
                                    <div className="order-detail__badges">
                                        <span className={resolveStatusChip(selectedOrder.status)}>{statusLabels[selectedOrder.status]}</span>
                                        <span className={resolvePaymentChip(selectedOrder.paymentStatus)}>
                                            {selectedOrder.paymentStatus === 'PAID' ? 'Pagato' : selectedOrder.paymentStatus === 'REFUNDED' ? 'Rimborsato' : 'Da incassare'}
                                        </span>
                                        <span className={`order-channel order-channel--${selectedOrder.channel.toLowerCase()}`}>{resolveChannelLabel(selectedOrder.channel)}</span>
                                    </div>
                                </div>
                                <div className="order-detail__actions">
                                    <button type="button" onClick={() => handleStatusChange(selectedOrder, 'ACKNOWLEDGED')}>Conferma</button>
                                    <button type="button" onClick={() => handleStatusChange(selectedOrder, 'PREPARING')}>Invia in preparazione</button>
                                    {escalationAction && (
                                        <button type="button" onClick={() => handleStatusChange(selectedOrder, escalationAction.status)}>{escalationAction.label}</button>
                                    )}
                                    <button type="button" className="outline" onClick={() => handleStatusChange(selectedOrder, 'CANCELLED')}>Annulla</button>
                                </div>
                            </header>

                            <section className="detail-section">
                                <header className="detail-section__header">
                                    <h3>Cliente & recapiti</h3>
                                    <span>{formatTime(selectedOrder.createdAt)} · {selectedOrder.customer.phone ?? 'N° non disponibile'}</span>
                                </header>
                                <div className="detail-grid">
                                    <div>
                                        <p className="detail-label">Nome</p>
                                        <p className="detail-value">{selectedOrder.customer.name}</p>
                                    </div>
                                    {selectedOrder.customer.email && (
                                        <div>
                                            <p className="detail-label">Email</p>
                                            <p className="detail-value">{selectedOrder.customer.email}</p>
                                        </div>
                                    )}
                                    {selectedOrder.customer.address && (
                                        <div className="address-block">
                                            <p className="detail-label">Indirizzo consegna</p>
                                            <p className="detail-value">
                                                {selectedOrder.customer.address.line1}
                                                {selectedOrder.customer.address.line2 && <><br />{selectedOrder.customer.address.line2}</>}
                                                <br />{selectedOrder.customer.address.postalCode} · {selectedOrder.customer.address.city}
                                            </p>
                                            {selectedOrder.customer.address.notes && (
                                                <p className="detail-hint">Nota citofono: {selectedOrder.customer.address.notes}</p>
                                            )}
                                        </div>
                                    )}
                                    {selectedOrder.customer.companyName && (
                                        <div>
                                            <p className="detail-label">Dati fatturazione</p>
                                            <p className="detail-value">{selectedOrder.customer.companyName}<br />P.IVA: {selectedOrder.customer.taxId ?? '—'}</p>
                                        </div>
                                    )}
                                </div>
                            </section>

                            <section className="detail-section">
                                <header className="detail-section__header">
                                    <h3>Contesto servizio</h3>
                                    <span>Informazioni operative per il canale {resolveChannelLabel(selectedOrder.channel).toLowerCase()}.</span>
                                </header>
                                <div className="service-grid">
                                    {selectedOrder.scheduledAt && (
                                        <article className="service-tile">
                                            <h4>Programmazione</h4>
                                            <p>{formatDateTime(selectedOrder.scheduledAt)}</p>
                                            <small>Orario previsto</small>
                                        </article>
                                    )}

                                    {selectedOrder.channel === 'DINE_IN' && selectedOrder.dineIn && (
                                        <>
                                            <article className="service-tile">
                                                <h4>Tavolo {selectedOrder.dineIn.table}</h4>
                                                <p>{selectedOrder.dineIn.covers} coperti</p>
                                                {selectedOrder.dineIn.area && <small>Area: {selectedOrder.dineIn.area}</small>}
                                                {selectedOrder.dineIn.checkInAt && <small>Check-in {formatTime(selectedOrder.dineIn.checkInAt)}</small>}
                                            </article>
                                            {(selectedOrder.dineIn.waiter || selectedOrder.dineIn.seatings) && (
                                                <article className="service-tile">
                                                    <h4>Servizio sala</h4>
                                                    {selectedOrder.dineIn.waiter && <p>Cameriere: {selectedOrder.dineIn.waiter}</p>}
                                                    {selectedOrder.dineIn.seatings && <small>{selectedOrder.dineIn.seatings}</small>}
                                                </article>
                                            )}
                                            {selectedOrder.dineIn.note && (
                                                <article className="service-tile">
                                                    <h4>Note tavolo</h4>
                                                    <p>{selectedOrder.dineIn.note}</p>
                                                </article>
                                            )}
                                        </>
                                    )}

                                    {selectedOrder.channel === 'TAKEAWAY' && selectedOrder.takeaway && (
                                        <>
                                            <article className="service-tile">
                                                <h4>Ritiro</h4>
                                                <p>{selectedOrder.takeaway.pickupAt ? formatDateTime(selectedOrder.takeaway.pickupAt) : 'Orario da definire'}</p>
                                                {selectedOrder.takeaway.pickupName && <small>Intestato a {selectedOrder.takeaway.pickupName}</small>}
                                            </article>
                                            <article className="service-tile">
                                                <h4>Modalità</h4>
                                                <p>{selectedOrder.takeaway.contactless ? 'Contactless' : 'Consegna al banco'}</p>
                                                {selectedOrder.takeaway.packagingNotes && <small>{selectedOrder.takeaway.packagingNotes}</small>}
                                            </article>
                                        </>
                                    )}

                                    {selectedOrder.channel === 'DELIVERY' && (
                                        <>
                                            {typeof selectedOrder.serviceFee === 'number' && (
                                                <article className="service-tile">
                                                    <h4>Costi servizio</h4>
                                                    <p>{formatCurrency(selectedOrder.serviceFee)}</p>
                                                    <small>Fee piatta consegna</small>
                                                </article>
                                            )}
                                            {selectedOrder.notes?.delivery && (
                                                <article className="service-tile">
                                                    <h4>Istruzioni rider</h4>
                                                    <p>{selectedOrder.notes.delivery}</p>
                                                </article>
                                            )}
                                        </>
                                    )}
                                </div>
                            </section>

                            <section className="detail-section">
                                <header className="detail-section__header">
                                    <h3>Piatti & preparazione</h3>
                                    <span>{selectedOrder.items.length} righe · Totale {formatCurrency(selectedOrder.total)}</span>
                                </header>
                                <ul className="items-list">
                                    {selectedOrder.items.map(item => (
                                        <li key={item.id} className="items-list__row">
                                            <div className="items-list__info">
                                                <strong>{item.quantity}× {item.name}</strong>
                                                <small>{formatCurrency(item.total)} · {item.station ?? 'Stazione generica'}</small>
                                                {item.modifiers && item.modifiers.length > 0 && (
                                                    <ul className="items-list__modifiers">
                                                        {item.modifiers.map(modifier => (
                                                            <li key={modifier}>• {modifier}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                                {item.note && (
                                                    <p className="items-list__note">{item.note}</p>
                                                )}
                                            </div>
                                            <span className="items-list__price">{formatCurrency(item.price)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>

                            {(selectedOrder.notes && (
                                selectedOrder.notes.customer || selectedOrder.notes.kitchen || selectedOrder.notes.delivery || selectedOrder.notes.billing
                            )) && (
                                    <section className="detail-section">
                                        <header className="detail-section__header">
                                            <h3>Note operative</h3>
                                        </header>
                                        <div className="notes-grid">
                                            {selectedOrder.notes.kitchen && (
                                                <article>
                                                    <h4>Per la cucina</h4>
                                                    <p>{selectedOrder.notes.kitchen}</p>
                                                </article>
                                            )}
                                            {selectedOrder.notes.delivery && (
                                                <article>
                                                    <h4>Per il rider</h4>
                                                    <p>{selectedOrder.notes.delivery}</p>
                                                </article>
                                            )}
                                            {selectedOrder.notes.customer && (
                                                <article>
                                                    <h4>Richieste cliente</h4>
                                                    <p>{selectedOrder.notes.customer}</p>
                                                </article>
                                            )}
                                            {selectedOrder.notes.billing && (
                                                <article>
                                                    <h4>Amministrazione</h4>
                                                    <p>{selectedOrder.notes.billing}</p>
                                                </article>
                                            )}
                                        </div>
                                    </section>
                                )}

                            <section className="detail-section">
                                <header className="detail-section__header">
                                    <h3>Stato consegna</h3>
                                </header>
                                <div className="log-timeline">
                                    {selectedOrder.timeline.map(entry => (
                                        <div key={`${entry.status}-${entry.at}`} className="log-timeline__item">
                                            <div className="log-timeline__time">{formatTime(entry.at)}</div>
                                            <div className="log-timeline__content">
                                                <strong>{entry.label}</strong>
                                                <span>{statusLabels[entry.status]}</span>
                                                {entry.note && <small>{entry.note}</small>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {selectedOrder.courier && (
                                    <div className="courier-card">
                                        <header>
                                            <h4>Rider assegnato</h4>
                                            <span>{selectedOrder.courier.vehicle}</span>
                                        </header>
                                        <p>{selectedOrder.courier.name}</p>
                                        {selectedOrder.courier.phone && <p className="courier-card__phone">{selectedOrder.courier.phone}</p>}
                                        {typeof selectedOrder.courier.etaMinutes === 'number' && (
                                            <p className="courier-card__eta">ETA {selectedOrder.courier.etaMinutes} minuti</p>
                                        )}
                                        <div className="courier-card__actions">
                                            <button type="button">📞 Chiama</button>
                                            <button type="button" className="outline">Tracking</button>
                                        </div>
                                    </div>
                                )}
                            </section>

                            <section className="detail-section">
                                <header className="detail-section__header">
                                    <h3>Centro stampe</h3>
                                    <span>Configura stampe automatiche e manuali per questo ordine.</span>
                                </header>
                                {!printCenterEnabled ? (
                                    <div className="print-center__disabled">
                                        <p>Centro stampa disabilitato per questo tenant. Abilitalo dal pannello amministratore per visualizzare modelli e anteprime.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="print-center">
                                            {printTemplates.map(template => {
                                                const templateInfo = templatesByKind[template.key];
                                                const assignedDestinations = destinationsByKind[template.key] ?? [];
                                                const destinationsLabel = assignedDestinations.length > 0
                                                    ? assignedDestinations
                                                        .map(dest => {
                                                            const name = dest.name || dest.code || `ID ${dest.id}`;
                                                            return dest.isDefault ? `${name} (default)` : name;
                                                        })
                                                        .join(', ')
                                                    : null;
                                                return (
                                                    <article key={template.key} className="print-card">
                                                        <header>
                                                            <h4>{template.title}</h4>
                                                            <button
                                                                type="button"
                                                                className={autoPrintTemplates[template.key] ? 'toggle active' : 'toggle'}
                                                                onClick={() => toggleAutoPrint(template.key)}
                                                            >
                                                                {autoPrintTemplates[template.key] ? 'Auto' : 'Manuale'}
                                                            </button>
                                                        </header>
                                                        <p>{template.description}</p>
                                                        <small>Destinazione suggerita: {template.suggestedDestination}</small>
                                                        <small className="print-card__meta">
                                                            {templateInfo ? `Template attivo: ${templateInfo.name}` : 'Template non configurato'}
                                                        </small>
                                                        <small className="print-card__meta">
                                                            {destinationsLabel
                                                                ? `Stampanti collegate: ${destinationsLabel}`
                                                                : 'Nessuna stampante associata'}
                                                        </small>
                                                        <div className="print-card__actions">
                                                            <button
                                                                type="button"
                                                                className={`outline ${selectedPreviewTemplate === template.key ? 'outline--active' : ''}`}
                                                                onClick={() => handlePreviewTemplateSelect(template.key)}
                                                            >
                                                                👁️ Anteprima
                                                            </button>
                                                            <button type="button" onClick={() => selectedOrder && handlePrint(selectedOrder, template)}>🖨️ Stampa</button>
                                                            <button type="button" className="outline" onClick={() => handleOpenTemplateConfig(template.key)}>Modello</button>
                                                            <button
                                                                type="button"
                                                                className="outline"
                                                                onClick={() => handleOpenDestinationConfig(template.key)}
                                                            >
                                                                Imposta stampante
                                                            </button>
                                                        </div>
                                                    </article>
                                                );
                                            })}
                                        </div>
                                        <div className="print-preview">
                                            <header>
                                                <h4>Anteprima rapida</h4>
                                                <span>Mostra la struttura della stampa scelta.</span>
                                            </header>
                                            {previewHtml && !previewLoading && !previewError && previewData && (
                                                <div className="print-preview__actions">
                                                    <button
                                                        type="button"
                                                        onClick={handleDownloadPreviewPdf}
                                                        disabled={previewPdfLoading}
                                                    >
                                                        {previewPdfLoading ? 'Generazione PDF…' : '⬇️ Scarica PDF'}
                                                    </button>
                                                </div>
                                            )}
                                            <div className="print-preview__content">
                                                {previewLoading && (
                                                    <div className="print-preview__placeholder">Caricamento anteprima…</div>
                                                )}
                                                {!previewLoading && previewError && (
                                                    <div className="print-preview__placeholder print-preview__placeholder--error">
                                                        Anteprima non disponibile: {previewError}
                                                    </div>
                                                )}
                                                {!previewLoading && !previewError && previewData && (
                                                    <div className="print-preview__panel">
                                                        {renderPreviewMeta()}
                                                        {previewHtml ? (
                                                            <div className="print-preview__canvas">
                                                                <iframe
                                                                    key={previewData.generatedAt}
                                                                    className="print-preview__iframe"
                                                                    title="Anteprima stampa ordine"
                                                                    sandbox="allow-same-origin"
                                                                    srcDoc={previewHtml}
                                                                    loading="lazy"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="print-preview__section">
                                                                    <h5>Cliente</h5>
                                                                    <p>
                                                                        {(() => {
                                                                            const name = getStringField(previewCustomer, 'name')
                                                                                ?? getStringField(previewRoot, 'customerName')
                                                                                ?? 'Cliente';
                                                                            const phone = getStringField(previewCustomer, 'phone');
                                                                            const table = getStringField(previewRoot, 'tableNumber');
                                                                            if (table) {
                                                                                return `${name} · Tavolo ${table}${phone ? ` · ${phone}` : ''}`;
                                                                            }
                                                                            return `${name}${phone ? ` · ${phone}` : ''}`;
                                                                        })()}
                                                                    </p>
                                                                    {previewAddress && (
                                                                        <small>{(() => {
                                                                            const line1 = getStringField(previewAddress, 'line1');
                                                                            const city = getStringField(previewAddress, 'city');
                                                                            const postal = getStringField(previewAddress, 'postalCode');
                                                                            return [line1, city, postal].filter(Boolean).join(' · ');
                                                                        })()}</small>
                                                                    )}
                                                                </div>

                                                                <table className="print-preview__items">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Qtà</th>
                                                                            <th>Articolo</th>
                                                                            <th>Imponibile</th>
                                                                            <th>IVA</th>
                                                                            <th>Lordo</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {previewItems.map((item, index) => {
                                                                            const quantity = coerceNumber(item['quantity']) ?? 1;
                                                                            const name = (item['productName'] as string)
                                                                                ?? (item['name'] as string)
                                                                                ?? 'Articolo';
                                                                            const modifiers = Array.isArray(item['modifiers']) ? item['modifiers'] as string[] : [];
                                                                            return (
                                                                                <tr key={`${name}-${index}`}>
                                                                                    <td>{quantity}</td>
                                                                                    <td>
                                                                                        <span>{name}</span>
                                                                                        {modifiers.length > 0 && (
                                                                                            <em className="print-preview__modifiers">{modifiers.join(', ')}</em>
                                                                                        )}
                                                                                    </td>
                                                                                    <td>{formatCurrencySafe(item['totalNet'])}</td>
                                                                                    <td>{formatCurrencySafe(item['totalVat'])}</td>
                                                                                    <td>{formatCurrencySafe(item['totalGross'] ?? item['totalPrice'])}</td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                        {previewItems.length === 0 && (
                                                                            <tr>
                                                                                <td colSpan={5} className="print-preview__empty-row">Nessun articolo disponibile</td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>

                                                                <div className="print-preview__totals">
                                                                    <div>
                                                                        <span>Totale imponibile</span>
                                                                        <strong>{formatCurrencySafe(totalNet)}</strong>
                                                                    </div>
                                                                    <div>
                                                                        <span>IVA ({vatRate !== null ? `${(vatRate * 100).toFixed(0)}%` : '10%'})</span>
                                                                        <strong>{formatCurrencySafe(totalVat)}</strong>
                                                                    </div>
                                                                    <div>
                                                                        <span>Totale lordo</span>
                                                                        <strong>{formatCurrencySafe(totalGross)}</strong>
                                                                    </div>
                                                                </div>

                                                                {(deliveryGross || deliveryNet || deliveryVat) && (
                                                                    <div className="print-preview__totals print-preview__totals--muted">
                                                                        <div>
                                                                            <span>Consegna imponibile</span>
                                                                            <strong>{formatCurrencySafe(deliveryNet)}</strong>
                                                                        </div>
                                                                        <div>
                                                                            <span>Consegna IVA</span>
                                                                            <strong>{formatCurrencySafe(deliveryVat)}</strong>
                                                                        </div>
                                                                        <div>
                                                                            <span>Consegna lordo</span>
                                                                            <strong>{formatCurrencySafe(deliveryGross)}</strong>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {(subtotalGross !== null || subtotalNet !== null || subtotalVat !== null) && (
                                                                    <div className="print-preview__totals print-preview__totals--compact">
                                                                        <div>
                                                                            <span>Subtotale lordo</span>
                                                                            <strong>{formatCurrencySafe(subtotalGross)}</strong>
                                                                        </div>
                                                                        <div>
                                                                            <span>Subtotale imponibile</span>
                                                                            <strong>{formatCurrencySafe(subtotalNet)}</strong>
                                                                        </div>
                                                                        <div>
                                                                            <span>Subtotale IVA</span>
                                                                            <strong>{formatCurrencySafe(subtotalVat)}</strong>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                                {!previewLoading && !previewError && !previewData && (
                                                    <div className="print-preview__placeholder">Seleziona un ordine per visualizzare l'anteprima.</div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </section>

                            <section className="detail-section">
                                <header className="detail-section__header">
                                    <h3>Azioni amministrative</h3>
                                    <span>Gestisci incassi, rimborsi e documenti fiscali.</span>
                                </header>
                                {adminNotice && (
                                    <div className={`admin-notice admin-notice--${adminNotice.type}`}>
                                        {adminNotice.message}
                                    </div>
                                )}
                                <div className="admin-actions">
                                    <button
                                        type="button"
                                        onClick={handleOpenRegisterPayment}
                                        disabled={!selectedOrder || paymentModalSubmitting}
                                    >
                                        Registra incasso POS
                                    </button>
                                    <button type="button">Invia ricevuta al cliente</button>
                                    <button type="button" className="outline">Genera fattura</button>
                                </div>
                            </section>
                        </div>
                    )}
                </main>
            </div>
            {paymentModalOpen && selectedOrder && (
                <div className="payment-modal__overlay" role="dialog" aria-modal="true" onClick={handleCloseRegisterPayment}>
                    <div className="payment-modal" onClick={event => event.stopPropagation()}>
                        <header>
                            <h4>Registra incasso</h4>
                            <button
                                type="button"
                                className="payment-modal__close"
                                onClick={handleCloseRegisterPayment}
                                aria-label="Chiudi"
                                disabled={paymentModalSubmitting}
                            >
                                ×
                            </button>
                        </header>
                        <p className="payment-modal__subtitle">
                            Importo ordine: {formatCurrency(selectedOrder.total ?? 0)} · Stato attuale: {selectedOrder.paymentStatus === 'PAID' ? 'Pagato' : 'Da incassare'}
                        </p>
                        {paymentModalError && <div className="payment-modal__error">{paymentModalError}</div>}
                        <form onSubmit={handleSubmitRegisterPayment} className="payment-modal__form">
                            <label>
                                Importo incassato
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={paymentModalForm.amount}
                                    onChange={event => setPaymentModalForm(prev => ({ ...prev, amount: event.target.value }))}
                                    placeholder="36.00"
                                    disabled={paymentModalSubmitting}
                                />
                            </label>
                            <label>
                                Metodo di pagamento
                                <select
                                    value={paymentModalForm.method}
                                    onChange={event => setPaymentModalForm(prev => ({ ...prev, method: event.target.value }))}
                                    disabled={paymentModalSubmitting}
                                >
                                    {registerPaymentMethods.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                Riferimento pagamento (opzionale)
                                <input
                                    type="text"
                                    value={paymentModalForm.reference}
                                    onChange={event => setPaymentModalForm(prev => ({ ...prev, reference: event.target.value }))}
                                    placeholder="ID POS / CRO"
                                    disabled={paymentModalSubmitting}
                                />
                            </label>
                            <label>
                                Note interne (opzionale)
                                <textarea
                                    value={paymentModalForm.note}
                                    onChange={event => setPaymentModalForm(prev => ({ ...prev, note: event.target.value }))}
                                    placeholder="Es. Incasso registrato al POS 2"
                                    disabled={paymentModalSubmitting}
                                />
                            </label>
                            <div className="payment-modal__footer">
                                <button
                                    type="button"
                                    className="payment-modal__button payment-modal__button--secondary"
                                    onClick={handleCloseRegisterPayment}
                                    disabled={paymentModalSubmitting}
                                >
                                    Annulla
                                </button>
                                <button
                                    type="submit"
                                    className="payment-modal__button payment-modal__button--primary"
                                    disabled={paymentModalSubmitting}
                                >
                                    {paymentModalSubmitting ? 'Salvataggio…' : 'Conferma incasso'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrdersManagementPage;
